import axios from 'axios'

// Import konfiguracji API
import config from '../config/api'

// Określenie bazowego adresu API
const API_URL = config.API_URL

console.log(`🌐 API client initializing with base URL: ${API_URL}`);

// Utworzenie instancji axios z konfiguracją
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  // Set a safe default; specific requests may override
  timeout: 300000,
  withCredentials: true
})

// Dodanie interceptorów dla requestów
const DEBUG_LOGS = config.ENV !== 'PRODUCTION'

apiClient.interceptors.request.use(
  (config) => {
    // Attach JWT token from localStorage to every request
    try {
      const token = localStorage.getItem('token')
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`
      }
    } catch {}

    if (DEBUG_LOGS) {
      console.log(`🌐 API Request: ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`);
    }
    // Don't log sensitive information in production
    const isLoginRequest = config.url?.includes('login');
    
    if (DEBUG_LOGS) {
      if (!isLoginRequest) {
        console.log(`🌐 Full URL:`, `${config.baseURL}${config.url}`);
        console.log(`🌐 Request headers:`, config.headers);
        if (config.params) console.log(`🌐 Request params:`, config.params);
        if (config.data) console.log(`🌐 Request data:`, config.data);
      } else {
        console.log(`🌐 Login request - data hidden for security`);
      }
    }
    return config;
  },
  (error) => {
    // Always log errors
    console.error('🌐 API Request error:', error);
    return Promise.reject(error);
  }
);

// Pomocnik: bezpieczne przekierowanie do /login po 401
function handleUnauthorized(url?: string) {
  try {
    const isLoginCall = !!url && url.includes('/login')
    const isAuthUserCheck = !!url && url.includes('/auth/user')

    // Public routes that don't require authentication
    const publicRoutes = ['/', '/login', '/register']
    const isPublicRoute = publicRoutes.some(route =>
      window.location.pathname === route || window.location.pathname.startsWith(route + '/')
    )

    // Don't redirect if:
    // 1. It's a login call
    // 2. It's a session check (/auth/user)
    // 3. User is on a public page
    if (!isLoginCall && !isAuthUserCheck && !isPublicRoute && typeof window !== 'undefined') {
      try { localStorage.removeItem('user') } catch {}
      if (!window.location.pathname.startsWith('/login')) {
        window.location.assign('/login')
      }
    }
  } catch {}
}

// Dodanie interceptora do obsługi odpowiedzi
apiClient.interceptors.response.use(
  (response) => {
    if (DEBUG_LOGS) {
      console.log(`🌐 API Response success for ${response.config.url}, status: ${response.status}`);
    }
    
    // Don't log sensitive information for login responses
    const isLoginRequest = response.config.url?.includes('login');
    
    if (DEBUG_LOGS) {
      if (!isLoginRequest) {
        console.log(`🌐 Response headers:`, response.headers);
        console.log(`🌐 Response data:`, response.data);
      } else {
        console.log(`🌐 Login response - data hidden for security`);
      }
    }
    
    // Zwróć tylko dane, aby uprościć obsługę w reszcie aplikacji
    return response;
  },
  (error) => {
    // Zostawiamy logowanie błędów niezależnie od ENV
    console.error('🌐 API Response error:', error.response || error.message);
    if (error.response) {
      console.error(`🌐 Error status: ${error.response.status}`);
      console.error(`🌐 Error data:`, error.response.data);
      console.error(`🌐 Error headers:`, error.response.headers);
      // Globalna obsługa 401: wyloguj i przenieś na /login
      if (error.response.status === 401) {
        handleUnauthorized(error.config?.url)
      }
    }
    return Promise.reject(error);
  }
)

export default apiClient

// Simple cached fetch of server timeouts from backend config
let cachedTimeouts: { low: number; high: number } | null = null
let timeoutsFetchInFlight: Promise<{ low: number; high: number } | null> | null = null

export async function getServerTimeouts(): Promise<{ low: number; high: number }> {
  if (cachedTimeouts) return cachedTimeouts
  if (timeoutsFetchInFlight) {
    const res = await timeoutsFetchInFlight
    return res || { low: 100, high: 270 }
  }
  timeoutsFetchInFlight = (async () => {
    try {
      // Endpoint returns environment config without secrets
      const resp = await apiClient.get('/config/environment')
      const cfg = resp?.data?.config || {}
      const low = Number(cfg.runpod_timeout_low_quality) || 100
      const high = Number(cfg.runpod_timeout_high_quality) || 270
      cachedTimeouts = { low, high }
      return cachedTimeouts
    } catch (e) {
      // Fallback to known defaults from config.yaml if not reachable
      return { low: 100, high: 270 }
    } finally {
      timeoutsFetchInFlight = null
    }
  })()
  const res = await timeoutsFetchInFlight
  return res || { low: 100, high: 270 }
}
